import { useState } from "react";
import { useTranslation } from "react-i18next";
import { api, formatApiErrorDetail } from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Mail, Send, Copy, Check, Gift, Sparkles } from "lucide-react";

const TRIAL_OPTIONS = [1, 3, 6, 12];

/**
 * Dialog for creating a NEW tenant invitation from the platform admin
 * panel. Reused from both PlatformTenants ("+ Yeni Tenant Davet Et")
 * and PlatformTenantInvitations pages.
 */
export default function TenantInviteDialog({ open, onOpenChange, onCreated }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    email: "",
    name: "",
    trial_months: 1,
    notes: "",
  });
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const reset = () => {
    setForm({ email: "", name: "", trial_months: 1, notes: "" });
    setResult(null);
    setCopied(false);
  };

  const submit = async () => {
    const email = form.email.trim();
    if (!email || !email.includes("@")) {
      toast.error(t("platform.invitations.email_required"));
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/platform/tenant-invitations", {
        email,
        name: form.name.trim() || null,
        trial_months: form.trial_months,
        notes: form.notes.trim() || null,
      });
      toast.success(t("platform.invitations.created"));
      setResult(data);
      onCreated?.(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const copyToken = async () => {
    if (!result?.token) return;
    try {
      await navigator.clipboard.writeText(result.token);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* no-op */
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) reset();
        onOpenChange(o);
      }}
    >
      <DialogContent
        className="sm:max-w-lg max-h-[90vh] overflow-y-auto"
        data-testid="tenant-invite-dialog"
      >
        {!result ? (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Gift className="h-5 w-5 text-violet-600" />
                {t("platform.invitations.new_title")}
              </DialogTitle>
              <DialogDescription>
                {t("platform.invitations.new_hint")}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div>
                <label className="mb-1 block text-xs font-semibold text-zinc-700">
                  {t("platform.invitations.email_label")}{" "}
                  <span className="text-red-500">*</span>
                </label>
                <Input
                  type="email"
                  data-testid="tenant-invite-email"
                  placeholder="founder@newcompany.com"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-zinc-700">
                  {t("platform.invitations.name_label")}
                  <span className="ml-1 text-zinc-400 font-normal">
                    ({t("platform.invitations.optional")})
                  </span>
                </label>
                <Input
                  data-testid="tenant-invite-name"
                  placeholder="John Founder"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-zinc-700">
                  {t("platform.invitations.trial_label")}
                </label>
                <Select
                  value={String(form.trial_months)}
                  onValueChange={(v) =>
                    setForm({ ...form, trial_months: parseInt(v, 10) })
                  }
                >
                  <SelectTrigger data-testid="tenant-invite-trial-months">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TRIAL_OPTIONS.map((m) => (
                      <SelectItem
                        key={m}
                        value={String(m)}
                        data-testid={`tenant-invite-trial-option-${m}`}
                      >
                        {t("platform.invitations.months", { count: m })}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="mt-1 text-[11px] text-zinc-500">
                  {t("platform.invitations.trial_hint")}
                </p>
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-zinc-700">
                  {t("platform.invitations.notes_label")}
                  <span className="ml-1 text-zinc-400 font-normal">
                    ({t("platform.invitations.optional")})
                  </span>
                </label>
                <Textarea
                  data-testid="tenant-invite-notes"
                  placeholder={t("platform.invitations.notes_placeholder")}
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  rows={2}
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="ghost" onClick={() => onOpenChange(false)}>
                {t("common.cancel")}
              </Button>
              <Button
                onClick={submit}
                disabled={saving}
                data-testid="tenant-invite-send-btn"
                className="bg-gradient-to-r from-violet-600 to-indigo-600 text-white hover:from-violet-700 hover:to-indigo-700"
              >
                <Send className="mr-1.5 h-4 w-4" />
                {saving ? t("common.loading") : t("platform.invitations.send")}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-emerald-600" />
                {t("platform.invitations.sent_title")}
              </DialogTitle>
              <DialogDescription>
                {t("platform.invitations.sent_hint", {
                  email: result.email,
                  months: result.trial_months,
                })}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-3" data-testid="tenant-invite-result">
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
                <div className="text-[10px] font-semibold uppercase tracking-wider text-emerald-800">
                  {t("platform.invitations.tenant_created")}
                </div>
                <div className="mt-0.5 font-medium text-emerald-900">
                  {result.tenant_name}
                </div>
                <div className="mt-1 text-[11px] text-emerald-700">
                  trial ends{" "}
                  {new Date(result.trial_ends_at).toLocaleDateString()}
                </div>
              </div>

              <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-3">
                <div className="mb-1 flex items-center justify-between text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
                  <span>{t("platform.invitations.token_label")}</span>
                  <button
                    type="button"
                    onClick={copyToken}
                    data-testid="tenant-invite-copy-token"
                    className="inline-flex items-center gap-1 rounded-md bg-white px-2 py-0.5 text-[10px] font-medium text-violet-700 ring-1 ring-violet-200 hover:bg-violet-50"
                  >
                    {copied ? (
                      <>
                        <Check className="h-3 w-3" /> {t("platform.invitations.copied")}
                      </>
                    ) : (
                      <>
                        <Copy className="h-3 w-3" /> {t("platform.invitations.copy")}
                      </>
                    )}
                  </button>
                </div>
                <div className="break-all font-mono text-[11px] text-zinc-800">
                  {result.token}
                </div>
              </div>

              <div className="flex items-start gap-2 rounded-lg bg-violet-50 p-3 text-xs text-violet-900">
                <Mail className="mt-0.5 h-4 w-4 shrink-0" />
                <div>
                  {t("platform.invitations.email_sent_notice", {
                    email: result.email,
                  })}
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={reset}
                data-testid="tenant-invite-another-btn"
              >
                {t("platform.invitations.invite_another")}
              </Button>
              <Button
                onClick={() => {
                  onOpenChange(false);
                  reset();
                }}
                data-testid="tenant-invite-done-btn"
              >
                {t("common.close")}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
